import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarPlus } from "lucide-react";
import { Alert, Button, Dialog } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import type { PatientSearchOption } from "@/hooks/usePatientSearchForQuickStart";
import { stayAccent } from "./theme";
import { ReservationTypeToggle } from "./ReservationTypeToggle";
import { ReservationPetPicker } from "./ReservationPetPicker";
import { ReservationPackSelect } from "./ReservationPackSelect";
import { ReservationSchedule } from "./ReservationSchedule";
import { ReservationDatesField } from "./ReservationDatesField";
import { stayDaySourceFromValues } from "./reservationDates";
import { useContractPlanReservation } from "./useContractPlanReservation";
import { usePlanDayOnDate } from "./usePlanDayOnDate";
import { HotelRatePanel } from "./HotelRatePanel";
import { ShiftPicker } from "./ShiftPicker";
import { useStayScheduleDefaults } from "@/hooks/useStayScheduleDefaults";
import { formatDateBR } from "@/lib/date-utils";
import { HotelBedSelect } from "./HotelBedSelect";
import { PlanAndDaysSection } from "./PlanAndDaysSection";
import { ReservationComanda } from "./ReservationComanda";
import { usePetReservationSummary } from "./usePetReservationSummary";
import { PetProfileCard } from "./PetProfileCard";
import { reservationSchema, type ReservationFormValues } from "./reservationForm";
import { useCreateStayReservation } from "./useCreateStayReservation";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { nextCalendarDate } from "./reservationScheduleDefaults";
import { countHotelNights } from "@/lib/daycare-hotel/stayBilling";
import { useReservationBillingOptions } from "@/hooks/useReservationBillingOptions";
import { buildAutomaticStayLines, calculateReservationBilling } from "./reservationBilling";
import { useReservationCapacityCheck } from "./useReservationCapacityCheck";
import { OffDayDecision } from "./OffDayDecision";
import { applyOffDayResolution, type OffDayResolution } from "./applyOffDayResolution";
import { selectActiveWeeklyPlan } from "./petPlanDays";
import { resolveOffDayDecision } from "@/lib/daycare-hotel/offDayDecision";
import { useDaycarePlanDays } from "@/pages/daycare-hotel/plans/useDaycarePlanDays";
import { resolveHotelPrice } from "@/lib/daycare-hotel/hotelPriceTier";
import { useStayPlanContracts } from "@/hooks/useStayPlanContracts";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { isStayDayConflict } from "./reservationConflict";
import { useStayPlanStatus } from "@/hooks/useStayPlanStatus";
import { getReservationPlanBlockingAlerts } from "./reservationPlanAlerts";
import { clearStayPlanAlertDismissals } from "../plans/stayPlanAlertDismissalState";
import { buildStayDayDates } from "@/lib/daycare-hotel/stayDays";

interface NewReservationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function NewReservationDialog({ open, onOpenChange }: NewReservationDialogProps) {
  const { i18n, t } = useTranslation("daycareHotel");
  const [selectedPet, setSelectedPet] = useState<PatientSearchOption | null>(null);
  const [capacityWarningOpen, setCapacityWarningOpen] = useState(false);
  const [removedAutomaticLineKeys, setRemovedAutomaticLineKeys] = useState<string[]>([]);
  const [reservationConflictOpen, setReservationConflictOpen] = useState(false);
  const [pendingReservationValues, setPendingReservationValues] = useState<ReservationFormValues | null>(null);
  const [planAcknowledgementOpen, setPlanAcknowledgementOpen] = useState(false);
  const [pendingPlanAcknowledgement, setPendingPlanAcknowledgement] = useState<ReservationFormValues | null>(null);
  const [offDayResolution, setOffDayResolution] = useState<OffDayResolution | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const today = useMemo(() => new Date().toLocaleDateString("en-CA"), []);
  const form = useForm<ReservationFormValues>({
    mode: "onChange",
    resolver: zodResolver(reservationSchema),
    defaultValues: { moduleType: "day_care", isOvernight: false, petId: "", checkInDate: today, checkOutDate: null, recurrence: null, billing: "avulso", hotelRoomId: null, hotelRoomBedId: null, stayPackIds: [], comanda: [], arrivalShift: null, departureShift: null, billingTiming: null, dateMode: "single", pickedDates: [], excludedDates: [], planDraft: null },
  });
  const createReservation = useCreateStayReservation();
  const { data: profile } = useProfile();
  const summary = usePetReservationSummary(form.watch("petId") || null);
  const { data: customerId } = useCurrentClinicCustomerId();
  const moduleType = form.watch("moduleType");
  const recurrence = form.watch("recurrence");
  const checkInDate = form.watch("checkInDate");
  const checkOutDate = form.watch("checkOutDate");
  const planChoice = form.watch("planChoice") ?? null;
  const selectedPlanContractId = planChoice === "existing" ? form.watch("stayPlanContractId") ?? null : null;
  const selectedPlanStatus = useStayPlanStatus({
    module: moduleType === "hotel" ? "hotel" : "daycare",
    petId: form.watch("petId") || null,
    date: checkInDate,
    contractId: selectedPlanContractId,
  }).status;
  const selectedPlanUnits = form.watch("stayPlanUnitsToUse");
  const hasSelectedPlan = planChoice === "existing" && selectedPlanContractId !== null;
  const planStatus = hasSelectedPlan ? selectedPlanStatus : null;
  const waivedStayCharges = form.watch("waivedStayCharges");
  const rates = useReservationBillingOptions({ module: moduleType === "hotel" ? "hotel" : "daycare", petId: form.watch("petId") || null, checkInDate });
  const capacityCheck = useReservationCapacityCheck({ module: moduleType === "hotel" ? "hotel" : "daycare", date: checkInDate, checkOutDate, petId: form.watch("petId") || null, roomId: form.watch("hotelRoomId") ?? null, bedId: form.watch("hotelRoomBedId") ?? null });

  const locale = i18n.language.startsWith("pt") ? "pt-BR" : "en-US";
  const accent = stayAccent(moduleType);
  const dateLabel = useMemo(
    () => (checkInDate ? new Intl.DateTimeFormat(locale, { day: "numeric", month: "long" }).format(new Date(`${checkInDate}T12:00:00`)) : ""),
    [checkInDate, locale],
  );
  const headerDate = useMemo(() => {
    const source = checkInDate ? new Date(`${checkInDate}T12:00:00`) : new Date();
    const formatted = new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(source);
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }, [checkInDate, locale]);
  const dateMode = form.watch("dateMode");
  const pickedDates = form.watch("pickedDates");
  const excludedDates = form.watch("excludedDates");
  const stayDates = useMemo(
    () => buildStayDayDates(stayDaySourceFromValues({ moduleType, checkInDate, checkOutDate, recurrence, dateMode, pickedDates, excludedDates })),
    [checkInDate, checkOutDate, dateMode, excludedDates, moduleType, pickedDates, recurrence],
  );
  const requestedDays = moduleType === "hotel" ? Math.max(1, countHotelNights(checkInDate, checkOutDate ?? "")) : Math.max(1, stayDates.length);
  const scheduleDefaults = useStayScheduleDefaults().data;
  const shiftMode = moduleType === "hotel" ? scheduleDefaults?.hotelShiftMode : scheduleDefaults?.daycareShiftMode;
  const usesShifts = shiftMode === "two";
  const shiftWindows = (() => {
    if (!scheduleDefaults) return undefined;
    const isHotel = moduleType === "hotel";
    return {
      arrival: {
        morning: `${isHotel ? scheduleDefaults.hotelCheckInFrom : scheduleDefaults.daycareCheckInFrom}–${isHotel ? scheduleDefaults.hotelCheckInTo : scheduleDefaults.daycareCheckInTo}`,
        evening: `${isHotel ? scheduleDefaults.hotelEveningCheckInFrom : scheduleDefaults.daycareEveningCheckInFrom}–${isHotel ? scheduleDefaults.hotelEveningCheckInTo : scheduleDefaults.daycareEveningCheckInTo}`,
      },
      departure: {
        morning: `${isHotel ? scheduleDefaults.hotelCheckOutFrom : scheduleDefaults.daycareCheckOutFrom}–${isHotel ? scheduleDefaults.hotelCheckOutTo : scheduleDefaults.daycareCheckOutTo}`,
        evening: `${isHotel ? scheduleDefaults.hotelEveningCheckOutFrom : scheduleDefaults.daycareEveningCheckOutFrom}–${isHotel ? scheduleDefaults.hotelEveningCheckOutTo : scheduleDefaults.daycareEveningCheckOutTo}`,
      },
    };
  })();
  const departureShift = form.watch("departureShift") ?? null;
  // O hotel pode cobrar na entrada ou no fim da estadia; a escolha é da reserva.
  const billingTiming = form.watch("billingTiming") ?? "check_in";
  const isOvernight = form.watch("isOvernight");
  const planDraft = form.watch("planDraft") ?? null;
  const contractPlanReservation = useContractPlanReservation();
  const planDayOnDate = usePlanDayOnDate({
    petId: form.watch("petId") || null,
    date: checkInDate,
    enabled: moduleType === "day_care" && !planDraft,
  }).data ?? false;
  // A faixa de hotel depende da matrícula de CRECHE do pet, não do contrato de
  // hotel que `planStatus` traz numa reserva de hotel — são coisas diferentes.
  const petId = form.watch("petId") || null;
  const daycareContracts = useStayPlanContracts(
    "daycare",
    profile?.current_clinic_id ?? null,
    petId ? [petId] : [],
  ).data;
  // Pet com plano semanal: a data decide a cobertura. Dia do plano consome o
  // plano; fora dele, o atendente escolhe no aviso abaixo.
  // Antes do início o plano ainda não vale: a data segue o fluxo sem plano.
  const activeWeeklyPlan = moduleType === "day_care" && !planDraft ? selectActiveWeeklyPlan(daycareContracts ?? []) : null;
  const weeklyPlan = activeWeeklyPlan && checkInDate >= activeWeeklyPlan.startsOn ? activeWeeklyPlan : null;
  const weeklyPlanDays = useDaycarePlanDays(weeklyPlan?.id ?? null).data ?? [];
  const isOffDay = Boolean(
    weeklyPlan && checkInDate
      && resolveOffDayDecision({
        targetDate: checkInDate,
        fixedWeekdays: weeklyPlan.fixedWeekdays,
        planDays: weeklyPlanDays,
        weekInterval: weeklyPlan.weekInterval,
        startsOn: weeklyPlan.startsOn,
      }).isOffDay,
  );
  const offDayPackages = rates.assignedContracts
    .filter((contract) => contract.kind === "day_package" && (contract.remainingDays ?? 0) > 0)
    .map((contract) => ({ contractId: contract.contractId, name: contract.name, remainingDays: contract.remainingDays ?? 0 }));
  const daycareRate = rates.dailyRates?.daycareDailyRate ?? null;
  const adHocPriceLabel = daycareRate === null
    ? null
    : new Intl.NumberFormat(locale, { style: "currency", currency: "BRL" }).format(daycareRate);

  useEffect(() => {
    setOffDayResolution(null);
  }, [checkInDate, petId]);

  useEffect(() => {
    if (!usesShifts) return;
    // Na creche o comum é chegar de manhã e sair à noite; no hotel cada estadia
    // é de um jeito, então lá a escolha começa em branco.
    if (moduleType !== "day_care") return;
    if (!form.getValues("arrivalShift")) form.setValue("arrivalShift", "morning", { shouldValidate: true });
    if (!form.getValues("departureShift")) form.setValue("departureShift", "evening", { shouldValidate: true });
  }, [form, moduleType, usesShifts]);

  const weeklyPlanId = weeklyPlan?.id ?? null;
  const weeklyPlanName = weeklyPlan?.planName ?? null;
  useEffect(() => {
    if (!weeklyPlanId) return;
    // A comanda precisa refletir a escolha já no formulário, antes do submit.
    const coverage: { choice: "existing" | "avulso" | null; contractId: string | null; name: string | null } = !isOffDay
      ? { choice: "existing", contractId: weeklyPlanId, name: weeklyPlanName }
      : offDayResolution?.option === "swap_week"
        ? { choice: "existing", contractId: weeklyPlanId, name: weeklyPlanName }
        : offDayResolution?.option === "ad_hoc"
          ? offDayResolution.packageContractId
            ? { choice: "existing", contractId: offDayResolution.packageContractId, name: null }
            : { choice: "avulso", contractId: null, name: null }
          : { choice: null, contractId: null, name: null };
    form.setValue("planChoice", coverage.choice, { shouldValidate: true });
    form.setValue("stayPlanContractId", coverage.contractId, { shouldValidate: true });
    form.setValue("stayPlanUnitsToUse", coverage.choice === "existing" ? 1 : null, { shouldValidate: true });
    form.setValue("stayPlanName", coverage.name, { shouldValidate: true });
    form.setValue("billing", coverage.choice === "avulso" ? "avulso" : "plan", { shouldValidate: true });
  }, [form, isOffDay, offDayResolution, weeklyPlanId, weeklyPlanName]);
  const hasActiveDaycarePlan = Boolean(
    daycareContracts?.some((contract) => contract.status === "active" && contract.usageRule !== null),
  );
  const hotelPrice = resolveHotelPrice({
    standard: rates.dailyRates?.hotelNightlyRate ?? null,
    withDaycarePlan: rates.dailyRates?.hotelNightlyRateWithDaycarePlan ?? null,
    hasActiveDaycarePlan,
  });
  const dailyRate = moduleType === "hotel" ? hotelPrice.amount : rates.dailyRates?.daycareDailyRate ?? null;
  const hotelTierLabel = moduleType === "hotel" ? t(`priceTier.${hotelPrice.tier}`) : null;
  const automaticLines = useMemo(() => {
    if (!planChoice) return [];

    const billing = calculateReservationBilling({
      moduleType,
      requestedDays,
      dailyRate,
      plan: planChoice === "existing" ? { origin: "existing", availableDays: selectedPlanUnits ?? requestedDays, price: 0 } : null,
    });

    return buildAutomaticStayLines({
      moduleType,
      requestedDays,
      dailyRate,
      // O rótulo some quando a tela fecha; a linha da comanda tem de registrar
      // a faixa aplicada, e o nome do item é onde isso cabe sem migration.
      dailyServiceName:
        moduleType === "hotel" ? `${t("hotelNightService")} (${hotelTierLabel})` : t("daycareService"),
      adHocDailyName:
        moduleType === "hotel" ? `${t("hotelNightService")} (${hotelTierLabel})` : t("daycareDailyAdHoc"),
      billing,
      includeUncoveredCharge: planChoice === "avulso",
      stayDates,
      waived: waivedStayCharges,
    });
  }, [
    dailyRate,
    hotelTierLabel,
    moduleType,
    planChoice,
    requestedDays,
    selectedPlanUnits,
    stayDates,
    waivedStayCharges,
    t,
  ]);
  const visibleAutomaticLines = automaticLines
    .filter((line) => line.status !== "paid_by_plan")
    .filter((line) => !removedAutomaticLineKeys.includes(`${line.itemName}-${line.status}`));

  const closeAfterSave = () => {
    form.reset();
    setSelectedPet(null);
    setRemovedAutomaticLineKeys([]);
    setPlanAcknowledgementOpen(false);
    setPendingPlanAcknowledgement(null);
    setOffDayResolution(null);
    onOpenChange(false);
  };

  const persistReservation = async (values: ReservationFormValues) => {
    if (weeklyPlan && isOffDay && offDayResolution) {
      // A escolha só mexe no plano aqui, depois de todas as confirmações.
      const outcome = await applyOffDayResolution({
        contractId: weeklyPlan.id,
        fixedWeekdays: weeklyPlan.fixedWeekdays,
        targetDate: values.checkInDate,
        usageLimit: weeklyPlan.usageLimit,
        resolution: offDayResolution,
      });
      if (outcome.next === "none") {
        // Mudar os dias já gera a diária pelo plano; criar reserva duplicaria.
        queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts"] });
        queryClient.invalidateQueries({ queryKey: ["stay-schedule"] });
        queryClient.invalidateQueries({ queryKey: ["daycare-hotel"] });
        toast({ title: t("offDay.planDaysUpdated") });
        closeAfterSave();
        return;
      }
      values = {
        ...values,
        planChoice: outcome.planChoice,
        stayPlanContractId: outcome.contractId,
        stayPlanUnitsToUse: outcome.planChoice === "existing" ? 1 : null,
        billing: outcome.planChoice === "avulso" ? "avulso" : "plan",
      };
    }
    await createReservation.mutateAsync({
      values: { ...values, comanda: values.comanda ?? [] },
      owner: selectedPet ? { id: selectedPet.ownerId, phone: selectedPet.ownerPrimaryPhone } : null,
      automaticLines: visibleAutomaticLines,
    });
    if (planStatus?.contract?.id && typeof window !== "undefined") {
      try {
        clearStayPlanAlertDismissals(window.localStorage, {
          day: values.checkInDate,
          petId: values.petId,
          contractId: planStatus.contract.id,
        });
      } catch {
        // Ignore browsers that block localStorage; reservation creation already succeeded.
      }
    }
    closeAfterSave();
  };

  const hasReservationConflict = async (values: ReservationFormValues) => {
    const clinicId = profile?.current_clinic_id;
    if (!clinicId) return false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any)
      .from("stay_reservation_days")
      .select("stay_date, status")
      .eq("clinic_id", clinicId)
      .eq("pet_id", values.petId)
      .eq("stay_date", values.checkInDate);
    if (error) throw error;
    return ((data ?? []) as Array<{ stay_date: string; status: string }>).some((day) => isStayDayConflict({
      stayDate: day.stay_date,
      status: day.status,
    }, values.checkInDate));
  };

  const checkConflictAndPersist = async (values: ReservationFormValues) => {
    if (await hasReservationConflict(values)) {
      setPendingReservationValues(values);
      setReservationConflictOpen(true);
      return;
    }
    await persistReservation(values);
  };

  const checkPlanAndContinue = async (values: ReservationFormValues) => {
    // No dia fora do plano a cota da semana não se aplica: a troca não muda o
    // total e a diária extra não usa a cota. Mensalidade vencida continua valendo.
    const blockingAlerts = getReservationPlanBlockingAlerts(planStatus)
      .filter((alert) => !(isOffDay && alert === "week_quota_exceeded"));
    if (blockingAlerts.length > 0) {
      setPendingPlanAcknowledgement(values);
      setPlanAcknowledgementOpen(true);
      return;
    }
    await checkConflictAndPersist(values);
  };

  const submit = form.handleSubmit(async (values) => {
    if (values.planDraft) {
      // O plano é a reserva: contrato, diárias e comanda saem numa transação só.
      await contractPlanReservation.mutateAsync({
        draft: values.planDraft,
        petId: values.petId,
        ownerId: selectedPet?.ownerId ?? values.ownerId ?? null,
        stayPackIds: values.stayPackIds,
        lines: values.comanda ?? [],
        chargeInComanda: values.stayPlanChargeInComanda,
      });
      form.reset();
      setSelectedPet(null);
      setRemovedAutomaticLineKeys([]);
      onOpenChange(false);
      return;
    }
    if (capacityCheck.data?.length) {
      setCapacityWarningOpen(true);
      return;
    }
    await checkPlanAndContinue(values);
  });

  return (
    <Dialog
      className="akuna-dialog--compact max-w-5xl"
      closeLabel={t("closeReservation")}
      icon={
        <span
          className="flex h-11 w-11 items-center justify-center rounded-xl"
          style={{ background: accent.soft, color: accent.solid }}
        >
          <CalendarPlus aria-hidden="true" size={22} />
        </span>
      }
      header={<span>{headerDate}</span>}
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          form.reset();
          setSelectedPet(null);
          setRemovedAutomaticLineKeys([]);
          setPlanAcknowledgementOpen(false);
          setPendingPlanAcknowledgement(null);
          setOffDayResolution(null);
        }
        onOpenChange(nextOpen);
      }}
      title={t("newReservationTitle")}
      mode="adaptive"
      footer={
        <div className="flex w-full items-center justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button disabled={!form.formState.isValid || createReservation.isPending || contractPlanReservation.isPending || (planDayOnDate && !planDraft) || (isOffDay && !offDayResolution)} loading={createReservation.isPending || contractPlanReservation.isPending} onClick={submit}>{t("createReservation")}</Button>
        </div>
      }
    >
      <div className="grid gap-5">
        <div className="grid items-start gap-3 sm:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)]">
          <ReservationTypeToggle value={moduleType} onChange={(value) => {
          form.setValue("moduleType", value, { shouldValidate: true });
          form.setValue("isOvernight", value === "hotel");
          form.setValue("billing", "avulso", { shouldValidate: true });
          form.setValue("checkOutDate", value === "hotel" ? nextCalendarDate(form.getValues("checkInDate") || today) : null, { shouldValidate: true });
          }} />
          <ReservationSchedule form={form} />
        </div>
        {usesShifts ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <ShiftPicker
              label={t("shiftArrival")}
              value={form.watch("arrivalShift") ?? null}
              windowLabel={shiftWindows?.arrival}
              onChange={(shift) => form.setValue("arrivalShift", shift, { shouldValidate: true })}
            />
            <ShiftPicker
              label={t("shiftDeparture")}
              value={form.watch("departureShift") ?? null}
              windowLabel={shiftWindows?.departure}
              onChange={(shift) => form.setValue("departureShift", shift, { shouldValidate: true })}
            />
          </div>
        ) : null}
        <ReservationPetPicker value={selectedPet} onSelect={(option) => {
          setSelectedPet(option);
          form.setValue("petId", option?.petId ?? "", { shouldValidate: true });
          form.setValue("ownerId", option?.ownerId, { shouldValidate: true });
          form.setValue("stayPackIds", [], { shouldValidate: true });
        }} />
        {selectedPet && summary.isLoading ? <Skeleton className="h-52 rounded-[22px]" /> : null}
        {selectedPet && summary.isError ? <Alert variant="danger">{t("petProfile.loadError")}</Alert> : null}
        {selectedPet ? (
          <PetProfileCard petId={selectedPet.petId} customerId={customerId ?? null} summary={summary.data} planStatus={planStatus} planDay={checkInDate} />
        ) : null}
        {moduleType === "day_care" && selectedPet ? (
          <ReservationPackSelect
            date={checkInDate}
            petId={form.watch("petId") || null}
            value={form.watch("stayPackIds")}
            onChange={(packIds) => form.setValue("stayPackIds", packIds, { shouldValidate: true })}
          />
        ) : null}
        {moduleType === "hotel" && <HotelBedSelect value={{ hotelRoomId: form.watch("hotelRoomId") ?? null, hotelRoomBedId: form.watch("hotelRoomBedId") ?? null }} onChange={(value) => { form.setValue("hotelRoomId", value.hotelRoomId, { shouldValidate: true }); form.setValue("hotelRoomBedId", value.hotelRoomBedId, { shouldValidate: true }); }} />}
        {planDayOnDate && !planDraft && selectedPet ? (
          <Alert variant="warning">{t("planDays.conflict", { pet: selectedPet.petName, date: formatDateBR(checkInDate) })}</Alert>
        ) : null}
        {weeklyPlan && isOffDay && selectedPet ? (
          <OffDayDecision
            key={`${petId}-${checkInDate}`}
            mode="create"
            targetDate={checkInDate}
            petName={selectedPet.petName}
            fixedWeekdays={weeklyPlan.fixedWeekdays}
            planDays={weeklyPlanDays}
            planAnchor={weeklyPlan.firstDueOn ?? weeklyPlan.startsOn}
            weekInterval={weeklyPlan.weekInterval}
            planStartsOn={weeklyPlan.startsOn}
            packages={offDayPackages}
            adHocPriceLabel={adHocPriceLabel}
            value={offDayResolution}
            onChange={setOffDayResolution}
          />
        ) : null}
        <PlanAndDaysSection form={form} stayDates={stayDates} hasWeeklyPlan={Boolean(weeklyPlan)} />
        {moduleType === "day_care" && !planDraft && !weeklyPlan ? <ReservationDatesField form={form} /> : null}
        {moduleType === "hotel" ? (
          <div className="grid gap-1.5">
            <span className="akuna-field__label">{t("hotelBilling.reservationLabel")}</span>
            <div role="radiogroup" aria-label={t("hotelBilling.reservationLabel")} className="flex flex-wrap gap-1.5">
              {(["check_in", "check_out"] as const).map((timing) => (
                <button
                  key={timing}
                  type="button"
                  role="radio"
                  aria-checked={billingTiming === timing}
                  onClick={() => form.setValue("billingTiming", timing, { shouldValidate: true })}
                  className={`rounded-full border px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${billingTiming === timing ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-foreground hover:bg-muted"}`}
                >
                  {t(`hotelBilling.${timing}`)}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {moduleType === "hotel" ? (
          <HotelRatePanel
            standard={rates.dailyRates?.hotelNightlyRate ?? null}
            student={rates.dailyRates?.hotelNightlyRateWithDaycarePlan ?? null}
            applied={hotelPrice.tier}
            petName={selectedPet?.petName ?? ""}
            locale={locale}
          />
        ) : null}
        <ReservationComanda
          form={form}
          petName={selectedPet?.petName ?? ""}
          ownerPhone={selectedPet?.ownerPrimaryPhone ?? null}
          moduleLabel={moduleType === "hotel" ? t("typeHotel") : t("typeDaycare")}
          dateLabel={dateLabel}
          automaticLines={visibleAutomaticLines}
          onRemoveAutomaticLine={(line) => setRemovedAutomaticLineKeys((current) => [...new Set([...current, `${line.itemName}-${line.status}`])])}
        />
      </div>
      <AlertDialog open={capacityWarningOpen} onOpenChange={setCapacityWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("capacityWarning.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("capacityWarning.description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {(capacityCheck.data ?? []).map((violation) => {
              const label = violation.kind === "size"
                ? t(`capacity.size${violation.label.charAt(0).toUpperCase()}${violation.label.slice(1)}`)
                : violation.label;
              return <li key={`${violation.kind}-${violation.label}`}>{t(`capacityWarning.${violation.kind}`, { label, used: violation.used, limit: violation.limit })}</li>;
            })}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setCapacityWarningOpen(false); void checkPlanAndContinue(form.getValues()); }}>{t("capacityWarning.continue")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={planAcknowledgementOpen} onOpenChange={(nextOpen) => {
        setPlanAcknowledgementOpen(nextOpen);
        if (!nextOpen) setPendingPlanAcknowledgement(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("reservationPlanConfirmation.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("reservationPlanConfirmation.description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              const values = pendingPlanAcknowledgement;
              setPlanAcknowledgementOpen(false);
              setPendingPlanAcknowledgement(null);
              if (values) void checkConflictAndPersist(values);
            }}>
              {t("reservationPlanConfirmation.continue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={reservationConflictOpen} onOpenChange={setReservationConflictOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("reservationConflict.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("reservationConflict.description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setReservationConflictOpen(false);
              if (pendingReservationValues) void persistReservation(pendingReservationValues);
            }}>
              {t("reservationConflict.continue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
